// Small formatting helpers shared by the library and watch screens. Kept dependency-free
// (no date library in WP10's package.json) since these are the only two call sites.

/** "2 days ago" / "3 weeks ago" style relative time, coarse enough for card meta lines
 * (design/Main.dc.html, design/Watch.dc.html).
 *
 * Issue #7: fixed grammar ("1 second ago" not "1 second"), proper granularity through
 * weeks (no jumping from week→month), and "just now" at 5 seconds instead of 1.
 */
export function formatRelativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  const diffMs = now.getTime() - then;
  const sec = Math.max(0, Math.round(diffMs / 1000));

  if (sec < 5) return "just now";
  if (sec < 60) return sec === 1 ? "1 second ago" : `${sec} seconds ago`;
  if (sec < 3600) {
    const min = Math.floor(sec / 60);
    return min === 1 ? "1 minute ago" : `${min} minutes ago`;
  }
  if (sec < 86400) {
    const hr = Math.floor(sec / 3600);
    return hr === 1 ? "1 hour ago" : `${hr} hours ago`;
  }
  if (sec < 604800) {
    const day = Math.floor(sec / 86400);
    return day === 1 ? "1 day ago" : `${day} days ago`;
  }
  if (sec < 2592000) { // ~30 days
    const week = Math.floor(sec / 604800);
    return week === 1 ? "1 week ago" : `${week} weeks ago`;
  }
  if (sec < 31536000) { // ~365 days
    const month = Math.floor(sec / 2592000);
    return month === 1 ? "1 month ago" : `${month} months ago`;
  }
  const year = Math.floor(sec / 31536000);
  return year === 1 ? "1 year ago" : `${year} years ago`;
}

/** "6:41" / "1:11:08" — mm:ss, growing to h:mm:ss past an hour. Player and card durations. */
export function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

const VIS_LABEL: Record<string, string> = {
  private: "Private",
  specific: "People",
  team: "Team",
  org: "Org",
  public: "Public",
};

/** One icon per level, the VisibilityPicker's own (Share dialog), so every badge and control that
 * names a level draws it the same way. */
export const VISIBILITY_ICON: Record<string, string> = {
  private: "lock-simple",
  specific: "user",
  team: "users",
  org: "buildings",
  public: "globe",
};

export function visibilityIcon(vis: string): string {
  return VISIBILITY_ICON[vis] ?? "lock-simple";
}

export function visibilityLabel(vis: string): string {
  return VIS_LABEL[vis] ?? vis;
}

/** "1.2 GB" / "340 KB" — byte counts are always mono/tabular per the design system; this just
 * picks the unit. Used by the org admin usage dashboard and the operator console's org list,
 * both of which show `source_bytes`-derived totals from the server. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}
